/**
 * DocumentRowWithReferences
 *
 * Document row component that supports reference search.
 * When searching, fetches and displays matching references (phone numbers, addresses, etc.)
 * Uses existing useGetArtifactReferencesQuery hook to avoid duplication.
 */
import { useEffect, useMemo } from 'react';

import { ArtifactIcon } from '@/components/artifacts/components/ArtifactIcon';
import { useGetArtifactReferencesQuery } from '@/services/api/artifactDigestService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { highlightText } from '@/utils/searchUtils';

interface DocumentRowWithReferencesProps {
  artifactId: string;
  displayName: string;
  artifactType: ArtifactType;
  isImage: boolean;
  searchTerm: string;
  titleMatches: boolean;
  onClick?: () => void;
  /** Called when visibility changes due to search filtering */
  onVisibilityChange?: (artifactId: string, isVisible: boolean) => void;
}

export const DocumentRowWithReferences = ({
  artifactId,
  displayName,
  artifactType,
  isImage,
  searchTerm,
  titleMatches,
  onClick,
  onVisibilityChange,
}: DocumentRowWithReferencesProps) => {
  const term = searchTerm.trim().toLowerCase();

  // Only fetch references when there's a search term
  const { data: references = [], isLoading } = useGetArtifactReferencesQuery(
    artifactId,
    { skip: !term },
  );

  // Filter references that match the search term
  const matchedReferences = useMemo(() => {
    if (!term) return [];
    return references.filter((ref) => {
      const value = (ref.value || '').toLowerCase();
      const description = (ref.description || '').toLowerCase();
      const type = (ref.type || '').toLowerCase();
      return (
        value.includes(term) ||
        description.includes(term) ||
        type.includes(term)
      );
    });
  }, [references, term]);

  // Determine if this row should be visible
  const isVisible =
    !term || // No search = always visible
    titleMatches || // Title matches = visible
    (!isLoading && matchedReferences.length > 0); // References match = visible

  // Report visibility changes to parent for empty state tracking
  useEffect(() => {
    if (term && !isLoading) {
      onVisibilityChange?.(artifactId, isVisible);
    }
  }, [artifactId, term, isLoading, isVisible, onVisibilityChange]);

  // Don't render if searching and no matches
  if (term && !isVisible && !isLoading) {
    return null;
  }

  // Still loading references - don't render yet to avoid flicker
  if (term && !titleMatches && isLoading) {
    return null;
  }

  return (
    <div className="rounded px-2 py-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800">
      {/* Document row */}
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 text-left"
      >
        <div className="flex-shrink-0">
          <ArtifactIcon type={artifactType} isImage={isImage} />
        </div>
        <span
          className="truncate text-xs font-medium text-zinc-900 dark:text-white"
          title={displayName}
        >
          {term ? highlightText(displayName, searchTerm) : displayName}
        </span>
      </button>

      {/* Matched references */}
      {matchedReferences.length > 0 && (
        <div className="ml-6 mt-1.5 space-y-1">
          {matchedReferences.map((ref) => (
            <div
              key={ref.id}
              className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400"
            >
              <span className="min-w-0 truncate">
                {highlightText(ref.value || ref.description || '', searchTerm)}
              </span>
              {ref.type && (
                <span className="flex-shrink-0 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-600 dark:text-zinc-400">
                  {ref.type}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
